import { BigNumber } from '@ethersproject/bignumber'
import { Trans } from '@lingui/macro'
import { CurrencyAmount, Percent, Price, Token } from '@uniswap/sdk-core'
import { Position } from '@uniswap/v3-sdk'
import { useWeb3React } from '@web3-react/core'
import Badge from 'components/Badge'
import RangeBadge from 'components/Badge/RangeBadge'
import { ButtonPrimary } from 'components/Button'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import HoverInlineText from 'components/HoverInlineText'
import Loader from 'components/Loader'
import { RowBetween } from 'components/Row'
import { TransactionConfirmationContext } from 'contexts/TransactionConfirmationContext'
import { useToken } from 'hooks/Tokens'
import { useV3NFTPositionManagerContract, useV3StakerContract } from 'hooks/useContract'
import { useIncentiveConfig } from 'hooks/useIncentive'
import useIsTickAtLimit from 'hooks/useIsTickAtLimit'
import { usePool } from 'hooks/usePools'
import { PositionStakingState, usePositionStakingState } from 'hooks/usePositionStakingState'
import { useSingleCallResult } from 'lib/hooks/multicall'
import { useCallback, useContext, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Bound } from 'state/mint/v3/actions'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import styled from 'styled-components/macro'
import { HideSmall, MEDIA_WIDTHS, SmallOnly } from 'theme'
import { opacify } from 'theme/utils'
import { formatTickPrice } from 'utils/formatTickPrice'
import { getIncentiveKeyEncoded, getIncentiveStatus } from 'utils/incentives'
import { transactionErrorToUserReadableMessage } from 'utils/transactionErrorToUserReadableMessage'
import { unwrappedToken } from 'utils/unwrappedToken'

import { USDC_TESTNET, WRAPPED_NATIVE_CURRENCY } from '../../constants/tokens'

const PositionRow = styled(Link)`
  align-items: center;
  display: flex;
  cursor: pointer;
  user-select: none;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: ${({ theme }) => theme.textPrimary};
  padding: 16px;
  font-weight: 500;
  text-decoration: none;

  & > div:not(:first-child) {
    text-align: center;
  }
  :hover {
    background-color: ${({ theme }) => theme.hoverDefault};
  }

  @media screen and (min-width: ${MEDIA_WIDTHS.deprecated_upToSmall}px) {
    /* flex-direction: row; */
  }

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    flex-direction: column;
    row-gap: 8px;
  `};
`

const BadgeText = styled.div`
  font-weight: 500;
  font-size: 14px;
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    font-size: 12px;
  `};
`

const DataLineItem = styled.div`
  font-size: 14px;
`

const RangeLineItem = styled(DataLineItem)`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 4px;
  width: 100%;
`

const RewardLineItem = styled(DataLineItem)`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 4px;
  width: 100%;
  color: ${({ theme }) => theme.accentAction};
  font-weight: 500;
`

const DoubleArrow = styled.span`
  margin: 0 2px;
  color: ${({ theme }) => theme.textTertiary};
`

const RangeText = styled.span`
  padding: 0.25rem 0.25rem;
  border-radius: 8px;
`

const ExtentsText = styled.span`
  color: ${({ theme }) => theme.textTertiary};
  font-size: 14px;
  margin-right: 4px;
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    display: none;
  `};
`

const PrimaryPositionIdData = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const DataText = styled.div`
  font-weight: 600;
  font-size: 18px;

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    font-size: 18px;
  `};
`

const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  @media screen and (min-width: ${MEDIA_WIDTHS.deprecated_upToSmall}px) {
    margin-top: 0;
    align-self: center;
    min-width: 82px; /* Ensures the container doesn't collapse */
  }

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    justify-content: center;
    margin-top: 10px;
    width: 100%;
  `};
`

const PositionInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const ContentRow = styled(RowBetween)`
  @media screen and (min-width: ${MEDIA_WIDTHS.deprecated_upToSmall}px) {
    flex-direction: row;
    align-items: center;
  }

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    flex-direction: column;
  `};
`

const StakeButton = styled(ButtonPrimary)`
  height: 36px;
  padding: 8px 16px;
  font-size: 14px;
  width: auto;
  flex-shrink: 0;
`

const UnstakeButton = styled(ButtonPrimary)`
  height: 36px;
  padding: 8px 16px;
  font-size: 14px;
  width: auto;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.accentActionSoft};
  &:hover {
    background-color: ${({ theme }) => opacify(15, theme.accentAction)};
  }
`

const ClaimButton = styled(ButtonPrimary)`
  height: 36px;
  padding: 8px 16px;
  font-size: 14px;
  width: auto;
  flex-shrink: 0;
`

const WithdrawButton = styled(ButtonPrimary)`
  height: 36px;
  padding: 8px 16px;
  font-size: 14px;
  width: auto;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.accentActionSoft};
  &:hover {
    background-color: ${({ theme }) => opacify(15, theme.accentAction)};
  }
`

interface IncentivePositionListItemProps {
  token0: string
  token1: string
  fee: number
  liquidity: BigNumber
  tickLower: number
  tickUpper: number
  tokenId: BigNumber
  stakingInfo?: {
    owner: string
    numberOfStakes: number
    liquidity?: BigNumber
    secondsPerLiquidityInsideInitialX128?: BigNumber
  }
}

function getPriceOrderingFromPositionForUI(position?: Position): {
  priceLower?: Price<Token, Token>
  priceUpper?: Price<Token, Token>
  quote?: Token
  base?: Token
} {
  if (!position) {
    return {}
  }

  const token0 = position.amount0.currency
  const token1 = position.amount1.currency

  // if token0 is a dollar-stable asset, set it as the quote token
  const stables = [USDC_TESTNET]
  if (stables.some((stable) => stable.equals(token0))) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // if token1 is an ETH-/BTC-stable asset, set it as the base token
  // TODO: Add WBTC when available
  const bases = [...Object.values(WRAPPED_NATIVE_CURRENCY)] //,WBTC]
  if (bases.some((base) => base && base.equals(token1))) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // if both prices are below 1, invert
  if (position.token0PriceUpper.lessThan(1)) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // otherwise, just return the default
  return {
    priceLower: position.token0PriceLower,
    priceUpper: position.token0PriceUpper,
    quote: token1,
    base: token0,
  }
}

// Add a helper function to prevent event propagation for action buttons
const stopPropagation = (e: React.MouseEvent<HTMLElement>) => {
  e.preventDefault()
  e.stopPropagation()
}

export default function IncentivePositionListItem({
  token0: token0Address,
  token1: token1Address,
  fee: feeAmount,
  liquidity,
  tickLower,
  tickUpper,
  tokenId,
  stakingInfo,
}: IncentivePositionListItemProps) {
  const { account } = useWeb3React()
  const token0 = useToken(token0Address)
  const token1 = useToken(token1Address)
  const { incentiveId } = useParams<{ incentiveId: string }>()
  const incentive = useIncentiveConfig(incentiveId)
  const [depositing, setDepositing] = useState(false)
  const [staking, setStaking] = useState(false)
  const [unstaking, setUnstaking] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)
  const { setTransactionData } = useContext(TransactionConfirmationContext)

  // Get reward token
  const rewardToken = useToken(incentive?.key.rewardToken)

  const currency0 = token0 ? unwrappedToken(token0) : undefined
  const currency1 = token1 ? unwrappedToken(token1) : undefined

  // Get staker and position manager contracts
  const positionManager = useV3NFTPositionManagerContract()
  const stakerContract = useV3StakerContract()
  const addTransaction = useTransactionAdder()

  // construct Position from details returned
  const [, pool] = usePool(currency0 ?? undefined, currency1 ?? undefined, feeAmount)

  const position = useMemo(() => {
    if (pool) {
      return new Position({ pool, liquidity: liquidity.toString(), tickLower, tickUpper })
    }
    return undefined
  }, [liquidity, pool, tickLower, tickUpper])

  const tickAtLimit = useIsTickAtLimit(feeAmount, tickLower, tickUpper)

  // prices
  const { priceLower, priceUpper, quote, base } = getPriceOrderingFromPositionForUI(position)

  const currencyQuote = quote && unwrappedToken(quote)
  const currencyBase = base && unwrappedToken(base)

  // check if price is within range
  const outOfRange: boolean = pool ? pool.tickCurrent < tickLower || pool.tickCurrent >= tickUpper : false

  const removed = liquidity?.eq(0)

  // Use the staking state hook to determine if we can deposit
  const stakingState = usePositionStakingState(stakingInfo?.owner, stakingInfo?.liquidity, stakingInfo?.numberOfStakes)

  // Check if the incentive is currently active
  const isIncentiveActive = incentive ? getIncentiveStatus(incentive) === 'active' : false

  // Fetch earned rewards using useSingleCallResult
  const earnedRewardResult = useSingleCallResult(stakerContract, 'getRewardInfo', [
    incentive ? Object.values(incentive.key) : undefined,
    tokenId,
  ])

  // Convert reward BigNumber to CurrencyAmount
  const earnedReward = useMemo(() => {
    const rewardBigNumber = earnedRewardResult?.result?.reward
    if (!rewardBigNumber || !rewardToken) return undefined
    return CurrencyAmount.fromRawAmount(rewardToken, rewardBigNumber.toString())
  }, [earnedRewardResult?.result?.reward, rewardToken])

  // Handler for depositing position
  const handleDeposit = useCallback(async () => {
    if (
      !positionManager ||
      !stakerContract ||
      !incentive ||
      !liquidity ||
      !account ||
      stakingState !== PositionStakingState.NOT_DEPOSITED
    ) {
      return
    }

    setTransactionData({
      showConfirm: true,
      attempting: true,
      pendingText: `Staking position #${tokenId.toString()}...`,
      hash: '',
      txError: '',
    })

    try {
      setDepositing(true)

      // Encode the incentive key to pass as data to the safeTransferFrom
      const encodedIncentiveKey = getIncentiveKeyEncoded(incentive.key)

      // We'll use safeTransferFrom to transfer the NFT position to the V3 staker contract
      const tx = await positionManager['safeTransferFrom(address,address,uint256,bytes)'](
        account, // User's address (from)
        stakerContract.address, // V3 staker contract (to)
        tokenId, // Token ID of the position
        encodedIncentiveKey // Encoded incentive key as bytes data
      )

      setTransactionData((transactionData) => ({
        ...transactionData,
        showConfirm: true,
        attempting: false,
        hash: tx.hash,
      }))

      // Add transaction to state for user feedback
      addTransaction(tx, {
        type: TransactionType.DEPOSIT_LIQUIDITY_STAKING,
        token0Address,
        token1Address,
      })

      await tx.wait()
    } catch (error) {
      console.error('Error depositing position:', error)
      setTransactionData((transactionData) => ({
        ...transactionData,
        attempting: false,
        showConfirm: true,
        hash: '',
        txError: transactionErrorToUserReadableMessage(error, 'An error occurred while staking'),
      }))
    } finally {
      setDepositing(false)
    }
  }, [
    positionManager,
    stakerContract,
    incentive,
    liquidity,
    account,
    stakingState,
    tokenId,
    addTransaction,
    token0Address,
    token1Address,
    setTransactionData,
  ])

  // Handler for staking a deposited position
  const handleStake = useCallback(async () => {
    if (
      !stakerContract ||
      !incentive ||
      !liquidity ||
      !account ||
      (stakingState !== PositionStakingState.DEPOSITED && stakingState !== PositionStakingState.STAKED_IN_OTHER)
    ) {
      return
    }

    setTransactionData({
      showConfirm: true,
      attempting: true,
      pendingText: `Staking position #${tokenId.toString()}...`,
      hash: '',
      txError: '',
    })

    try {
      setStaking(true)

      // Call the stakeToken function on the staker contract
      const tx = await stakerContract.stakeToken(incentive.key, tokenId)

      setTransactionData((transactionData) => ({
        ...transactionData,
        showConfirm: true,
        attempting: false,
        hash: tx.hash,
      }))

      // Add transaction to state for user feedback
      addTransaction(tx, {
        type: TransactionType.DEPOSIT_LIQUIDITY_STAKING,
        token0Address,
        token1Address,
      })

      await tx.wait()
    } catch (error) {
      console.error('Error staking position:', error)
      setTransactionData((transactionData) => ({
        ...transactionData,
        attempting: false,
        showConfirm: true,
        hash: '',
        txError: transactionErrorToUserReadableMessage(error, 'An error occurred while staking'),
      }))
    } finally {
      setStaking(false)
    }
  }, [
    stakerContract,
    incentive,
    liquidity,
    account,
    stakingState,
    tokenId,
    addTransaction,
    token0Address,
    token1Address,
    setTransactionData,
  ])

  // Handler for unstaking a staked position
  const handleUnstake = useCallback(async () => {
    if (!stakerContract || !incentive || !account || stakingState !== PositionStakingState.STAKED) {
      return
    }

    setTransactionData({
      showConfirm: true,
      attempting: true,
      pendingText: `Unstaking position #${tokenId.toString()}...`,
      hash: '',
      txError: '',
    })

    try {
      setUnstaking(true)

      // Call the unstakeToken function on the staker contract
      const tx = await stakerContract.unstakeToken(incentive.key, tokenId)

      setTransactionData((transactionData) => ({
        ...transactionData,
        showConfirm: true,
        attempting: false,
        hash: tx.hash,
      }))

      // Add transaction to state for user feedback
      addTransaction(tx, {
        type: TransactionType.WITHDRAW_LIQUIDITY_STAKING,
        token0Address,
        token1Address,
      })

      await tx.wait()
    } catch (error) {
      console.error('Error unstaking position:', error)
      setTransactionData((transactionData) => ({
        ...transactionData,
        attempting: false,
        showConfirm: true,
        hash: '',
        txError: transactionErrorToUserReadableMessage(error, 'An error occurred while unstaking'),
      }))
    } finally {
      setUnstaking(false)
    }
  }, [
    stakerContract,
    incentive,
    account,
    stakingState,
    tokenId,
    addTransaction,
    token0Address,
    token1Address,
    setTransactionData,
  ])

  // Handler for claiming rewards
  const handleClaim = useCallback(async () => {
    if (!stakerContract || !incentive || !account || stakingState !== PositionStakingState.STAKED) {
      return
    }

    setTransactionData({
      showConfirm: true,
      attempting: true,
      pendingText: `Claiming rewards for position #${tokenId.toString()}...`,
      hash: '',
      txError: '',
    })

    try {
      setClaiming(true)

      // Create an array of transaction calldata
      const callDatas = [
        // 1. Unstake the token first to update reward amounts
        stakerContract.interface.encodeFunctionData('unstakeToken', [incentive.key, tokenId]),

        // 2. Claim all rewards
        stakerContract.interface.encodeFunctionData('claimReward', [
          incentive.key.rewardToken,
          account,
          0, // Amount to claim - 0 means claim all available rewards
        ]),
      ]

      // 3. If incentive is active, add stakeToken back to the calldata
      if (isIncentiveActive) {
        callDatas.push(stakerContract.interface.encodeFunctionData('stakeToken', [incentive.key, tokenId]))
      }

      // Execute the multicall transaction
      const tx = await stakerContract.multicall(callDatas)

      setTransactionData((transactionData) => ({
        ...transactionData,
        showConfirm: true,
        attempting: false,
        hash: tx.hash,
      }))

      // Add transaction to state for user feedback
      addTransaction(tx, {
        type: TransactionType.CLAIM,
        recipient: account,
      })

      await tx.wait()
    } catch (error) {
      console.error('Error claiming rewards:', error)
      setTransactionData((transactionData) => ({
        ...transactionData,
        attempting: false,
        showConfirm: true,
        hash: '',
        txError: transactionErrorToUserReadableMessage(error, 'An error occurred while claiming rewards'),
      }))
    } finally {
      setClaiming(false)
    }
  }, [stakerContract, incentive, account, stakingState, tokenId, isIncentiveActive, addTransaction, setTransactionData])

  // Add handleWithdraw function
  const handleWithdraw = useCallback(async () => {
    if (!stakerContract || !account || stakingState !== PositionStakingState.DEPOSITED) {
      return
    }

    setTransactionData({
      showConfirm: true,
      attempting: true,
      pendingText: `Withdrawing position #${tokenId.toString()}...`,
      hash: '',
      txError: '',
    })

    try {
      setWithdrawing(true)

      // Call the withdrawToken function on the staker contract
      const tx = await stakerContract.withdrawToken(tokenId, account, [])

      setTransactionData((transactionData) => ({
        ...transactionData,
        showConfirm: true,
        attempting: false,
        hash: tx.hash,
      }))

      // Add transaction to state for user feedback
      addTransaction(tx, {
        type: TransactionType.WITHDRAW_LIQUIDITY_STAKING,
        token0Address,
        token1Address,
      })

      await tx.wait()
    } catch (error) {
      console.error('Error withdrawing position:', error)
      setTransactionData((transactionData) => ({
        ...transactionData,
        attempting: false,
        showConfirm: true,
        hash: '',
        txError: transactionErrorToUserReadableMessage(error, 'An error occurred while withdrawing'),
      }))
    } finally {
      setWithdrawing(false)
    }
  }, [stakerContract, account, stakingState, tokenId, addTransaction, token0Address, token1Address, setTransactionData])

  // Rendering different buttons based on staking state
  const renderActionButtons = () => {
    switch (stakingState) {
      case PositionStakingState.NOT_DEPOSITED:
        return isIncentiveActive ? (
          <StakeButton
            onClick={(e) => {
              stopPropagation(e)
              handleDeposit()
            }}
            disabled={depositing}
          >
            {depositing ? <Trans>Staking...</Trans> : <Trans>Stake</Trans>}
          </StakeButton>
        ) : null
      case PositionStakingState.DEPOSITED:
        return (
          <>
            {isIncentiveActive ? (
              <StakeButton
                onClick={(e) => {
                  stopPropagation(e)
                  handleStake()
                }}
                disabled={staking}
              >
                {staking ? <Trans>Staking...</Trans> : <Trans>Stake</Trans>}
              </StakeButton>
            ) : null}
            <WithdrawButton
              onClick={(e) => {
                stopPropagation(e)
                handleWithdraw()
              }}
              disabled={withdrawing}
            >
              {withdrawing ? <Trans>Withdrawing...</Trans> : <Trans>Withdraw</Trans>}
            </WithdrawButton>
          </>
        )
      case PositionStakingState.STAKED_IN_OTHER:
        return isIncentiveActive ? (
          <StakeButton
            onClick={(e) => {
              stopPropagation(e)
              handleStake()
            }}
            disabled={staking}
          >
            {staking ? <Trans>Staking...</Trans> : <Trans>Stake</Trans>}
          </StakeButton>
        ) : null
      case PositionStakingState.STAKED:
        return (
          <>
            {earnedReward?.greaterThan(0) ? (
              <ClaimButton
                onClick={(e) => {
                  stopPropagation(e)
                  handleClaim()
                }}
                disabled={claiming}
              >
                {claiming ? <Trans>Claiming...</Trans> : <Trans>Claim</Trans>}
              </ClaimButton>
            ) : null}

            <UnstakeButton
              onClick={(e) => {
                stopPropagation(e)
                handleUnstake()
              }}
              disabled={unstaking}
            >
              {unstaking ? <Trans>Unstaking...</Trans> : <Trans>Unstake</Trans>}
            </UnstakeButton>
          </>
        )
      default:
        return null
    }
  }

  // Position details URL
  const positionSummaryLink = `/pool/${tokenId.toString()}`

  return (
    <PositionRow to={positionSummaryLink}>
      <ContentRow>
        <PositionInfo>
          <RowBetween>
            <PrimaryPositionIdData>
              <DoubleCurrencyLogo currency0={currencyBase} currency1={currencyQuote} size={18} margin />
              <DataText>
                &nbsp;{currencyQuote?.symbol}&nbsp;/&nbsp;{currencyBase?.symbol}
              </DataText>
              <Badge>
                <BadgeText>
                  <Trans>{new Percent(feeAmount, 1_000_000).toSignificant()}%</Trans>
                </BadgeText>
              </Badge>
              <RangeBadge removed={removed} inRange={!outOfRange} />
            </PrimaryPositionIdData>
          </RowBetween>

          {priceLower && priceUpper ? (
            <RangeLineItem>
              <RangeText>
                <ExtentsText>
                  <Trans>Min: </Trans>
                </ExtentsText>
                <Trans>
                  {formatTickPrice({
                    price: priceLower,
                    atLimit: tickAtLimit,
                    direction: Bound.LOWER,
                  })}{' '}
                  <HoverInlineText text={currencyQuote?.symbol} /> per{' '}
                  <HoverInlineText text={currencyBase?.symbol ?? ''} />
                </Trans>
              </RangeText>{' '}
              <HideSmall>
                <DoubleArrow>⟷</DoubleArrow>{' '}
              </HideSmall>
              <SmallOnly>
                <DoubleArrow>⟷</DoubleArrow>{' '}
              </SmallOnly>
              <RangeText>
                <ExtentsText>
                  <Trans>Max:</Trans>
                </ExtentsText>
                <Trans>
                  {formatTickPrice({
                    price: priceUpper,
                    atLimit: tickAtLimit,
                    direction: Bound.UPPER,
                  })}{' '}
                  <HoverInlineText text={currencyQuote?.symbol} /> per{' '}
                  <HoverInlineText maxCharacters={10} text={currencyBase?.symbol} />
                </Trans>
              </RangeText>
            </RangeLineItem>
          ) : (
            <Loader />
          )}

          {/* Display earned rewards only if they're greater than zero */}
          {stakingState === PositionStakingState.STAKED && earnedReward?.greaterThan(0) && (
            <RewardLineItem>
              <Trans>
                Earned: {earnedReward.toSignificant(6)} {rewardToken?.symbol}
              </Trans>
            </RewardLineItem>
          )}
        </PositionInfo>

        <ActionButtonsContainer>{renderActionButtons()}</ActionButtonsContainer>
      </ContentRow>
    </PositionRow>
  )
}
